import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Flower, Loader2, ChevronRight, ChevronLeft, Check } from "lucide-react";
import {
  FamilyTreeCtaModal, OptionalAccordion,
} from "./CelebrateSubmit.parts";
import InitialsAvatar from "@/components/InitialsAvatar";
import {
  Step1Who, Step2Life, Step3Photo, Step4Story,
  Step5Family, Step6Extras, Step7Review,
} from "./CelebrateSubmit.steps";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || "";

function loadRecaptcha() {
  if (!RECAPTCHA_SITE_KEY) return Promise.resolve(null);
  if (window.__wofyRecaptchaPromise) return window.__wofyRecaptchaPromise;
  window.__wofyRecaptchaPromise = new Promise((resolve, reject) => {
    if (window.grecaptcha?.ready) { window.grecaptcha.ready(() => resolve(window.grecaptcha)); return; }
    const s = document.createElement("script");
    s.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    s.async = true;
    s.onload = () => window.grecaptcha.ready(() => resolve(window.grecaptcha));
    s.onerror = () => reject(new Error("Failed"));
    document.head.appendChild(s);
  });
  return window.__wofyRecaptchaPromise;
}

const border = { borderColor: "var(--wofy-border)" };

export default function CelebrateSubmit() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [sp] = useSearchParams();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    memorial_type: (sp.get("type") === "death_announcement" ? "death_announcement" : "cherished_memorial"),
    // Names
    title: "",
    full_name: sp.get("name") || "",
    preferred_name: "",
    known_as: "",
    nicknames: "",
    maiden_name: "",
    family_name: "",
    other_names: "",
    // Location
    location: {
      country: sp.get("country") || "",
      state: sp.get("state") || "",
      county: "",
      city: sp.get("city") || "",
      zip: "",
    },
    // Portraits
    portraits: [],           // up to 5 base64/URLs
    primary_portrait_index: 0,
    cover_image: "",
    quote: "",
    short_bio: "",
    // Life
    birth_date: sp.get("birth") || "",
    birth_place: sp.get("birth_place") || "",
    death_date: sp.get("death") || "",
    death_place: sp.get("death_place") || "",
    age_at_death: "",
    cause_of_death: "",
    gender: "",
    nationality: "",
    citizenship: "",
    occupation: "",
    profession: "",
    education: "",
    military_service: "",
    religion: "",
    languages: "",
    // Marital
    is_married: false,
    // Long form
    obituary: "",
    biography: "",
    // Funeral
    funeral_home: "", funeral_date: "", funeral_time: "", funeral_location: "",
    funeral_livestream: "", funeral_dress: "", flowers_accepted: "yes", donations_preferred: "no",
    cemetery_name: "", cemetery_section: "", cemetery_plot: "",
    // Family tree
    family_tree_slug: "",
    linked_sp_id: "",
    // Meta
    privacy: "public",
    consent: false,
    linked_family_member_id: sp.get("from_family_member") || "",
  });
  const [privacyMap, setPrivacyMap] = useState({});
  const [spouses, setSpouses] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [charities, setCharities] = useState([]);
  const [submitter, setSubmitter] = useState({ name: user?.name || "", email: user?.email || "", phone: user?.phone || "" });
  const [suggestions, setSuggestions] = useState(null);
  const [suggesting, setSuggesting] = useState(false);
  const [ctaModal, setCtaModal] = useState(null);
  // Phase 2/3/4 additions
  const [familyDetails, setFamilyDetails] = useState({});
  const [bioDetails, setBioDetails] = useState({
    hobbies: "", talents: "", awards: "", organizations: "",
    clubs: "", favorite_quote: "", words_lived_by: "",
    community_service: "", volunteer: "",
  });
  const [services, setServices] = useState({
    viewing: "", wake: "", memorial_service: "",
    celebration_of_life: "", burial_date: "", burial_time: "",
    cremation_date: "", crematorium: "", ashes_resting_place: "",
    live_stream_url: "", online_meeting_url: "",
    funeral_director: "", gps: "",
  });
  const [funeralProgram, setFuneralProgram] = useState({
    pdf_url: "", order_of_service: "", hymns: "",
    readings: "", speakers: "", pallbearers: "",
    honorary_pallbearers: "", reception: "",
    parking: "", accommodation: "",
  });
  const [audioTracks, setAudioTracks] = useState([]);   // [{title, url, kind}]
  const [legacy, setLegacy] = useState({
    recipes: "", letters: "", journals: "",
    artwork: "", oral_histories: "", speeches: "",
  });
  const [places, setPlaces] = useState([]);   // [{kind, name, location_slug, note}]

  // ---- External genealogy site links (Ancestry.com, 23andMe, etc.) ----
  const [genealogyLinks, setGenealogyLinks] = useState({
    ancestry: "",
    myheritage: "",
    familysearch: "",
    twentythreeandme: "",
    findagrave: "",
    wikitree: "",
    billiongraves: "",
    geni: "",
    other: "",
  });

  // ---------- Wizard state (progressive disclosure) ----------
  const DRAFT_KEY = useMemo(
    () => `celebrate-draft-${sp.get("from_family_member") || sp.get("name") || "default"}`,
    [sp],
  );
  const [step, setStep] = useState(1);
  const [optionalOpen, setOptionalOpen] = useState(false);
  const [reviewEditFor, setReviewEditFor] = useState(null); // which step user is editing from review

  // Restore draft once, so accidental refresh / navigation doesn't wipe input.
  const restored = useRef(false);
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const d = JSON.parse(raw);
      // Portraits and cover_image are dropped from the draft (base64 data
      // URLs blow through the localStorage quota).
      if (d.form) setForm((f) => ({ ...f, ...d.form }));
      if (d.spouses) setSpouses(d.spouses);
      if (d.relationships) setRelationships(d.relationships);
      if (d.timeline) setTimeline(d.timeline);
      if (d.charities) setCharities(d.charities);
      if (d.familyDetails) setFamilyDetails(d.familyDetails);
      if (d.bioDetails) setBioDetails((b) => ({ ...b, ...d.bioDetails }));
      if (d.services) setServices((s) => ({ ...s, ...d.services }));
      if (d.funeralProgram) setFuneralProgram((f) => ({ ...f, ...d.funeralProgram }));
      if (d.audioTracks) setAudioTracks(d.audioTracks);
      if (d.legacy) setLegacy((l) => ({ ...l, ...d.legacy }));
      if (d.places) setPlaces(d.places);
      if (d.genealogyLinks) setGenealogyLinks((g) => ({ ...g, ...d.genealogyLinks }));
      if (d.privacyMap) setPrivacyMap(d.privacyMap);
      if (d.step) setStep(Math.min(3, Math.max(1, Number(d.step) || 1)));
      setTimeout(() => toast.info("Draft restored — pick up where you left off."), 600);
    } catch { /* ignore */ }
  }, [DRAFT_KEY]);

  // Autosave on every meaningful change. Debounce lightly so we don't
  // thrash localStorage while the user is typing.
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        const { portraits, cover_image, ...formLite } = form; // eslint-disable-line no-unused-vars
        const payload = {
          form: formLite,
          spouses, relationships, timeline, charities,
          familyDetails, bioDetails, services, funeralProgram,
          audioTracks, legacy, places, genealogyLinks, privacyMap, step,
        };
        localStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      } catch { /* quota / private mode — ignore */ }
    }, 400);
    return () => clearTimeout(t);
  }, [form, spouses, relationships, timeline, charities, familyDetails, bioDetails, services, funeralProgram, audioTracks, legacy, places, genealogyLinks, privacyMap, step, DRAFT_KEY]);

  const clearDraft = () => { try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } };

  // 3-stage flow: Remember → Tell Their Story → Preview & Publish.
  const STEPS = [
    { n: 1, key: "remember", label: "Remember",     title: "Remember them",        encouragement: "Just the essentials — name, place and dates. Everything else can wait." },
    { n: 2, key: "story",    label: "Their Story",  title: "Tell their story",     encouragement: "Every memory matters — even a few sentences is a beautiful start. AI can help." },
    { n: 3, key: "publish",  label: "Publish",      title: "Preview & publish",    encouragement: "See exactly how it will look, then publish — completely free." },
  ];

  const validateStep = (n) => {
    const errs = [];
    if (n === 1) {
      if (!form.full_name.trim()) errs.push("Full name is required.");
      if (!form.location.country) errs.push("Country is required.");
      if (!form.birth_date && !form.age_at_death) errs.push("Either date of birth OR age at death is required.");
    } else if (n === 3) {
      if (!form.consent) errs.push("Please accept the terms before publishing.");
    }
    return errs;
  };

  const next = () => {
    const errs = validateStep(step);
    if (errs.length) { errs.forEach((e) => toast.error(e)); return; }
    setStep((s) => (reviewEditFor ? STEPS.length : Math.min(STEPS.length, s + 1)));
    setReviewEditFor(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const back = () => {
    setStep((s) => Math.max(1, s - 1));
    setReviewEditFor(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goTo = (n) => {
    setStep(n);
    setReviewEditFor(n);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Death announcements are text-first — hide the photo section so we don't
  // guilt-trip the family into digging up a photo they may not have.
  const isPhotoStepSkipped = form.memorial_type === "death_announcement";
  const stepsVisible = STEPS;

  // ---------- AI biography suggestions (Step 4) ----------
  const [aiBusy, setAiBusy] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const requestAiBio = async (tone = "warm") => {
    if (!form.full_name.trim()) {
      toast.error("Add the full name on Step 1 first.");
      return;
    }
    setAiBusy(true);
    setAiSuggestions([]);
    try {
      const payload = {
        full_name: form.full_name,
        birth_year: (form.birth_date || "").slice(0, 4) || undefined,
        death_year: (form.death_date || "").slice(0, 4) || undefined,
        birth_place: form.birth_place || form.location?.city || undefined,
        death_place: form.death_place || undefined,
        occupation: form.occupation || undefined,
        religion: form.religion || undefined,
        hobbies: bioDetails.hobbies || undefined,
        awards: bioDetails.awards || undefined,
        family_summary: (spouses.filter((s) => s.name).map((s) => s.name).join(", ") || undefined),
        memorial_type: form.memorial_type,
        tone,
      };
      const { data } = await http.post("/celebrate/suggest-biography", payload);
      setAiSuggestions(data.suggestions || []);
      toast.success("AI drafted a few options — tap one to insert.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" && detail.length < 200 ? detail : "AI is temporarily unavailable — please try again later.");
    } finally {
      setAiBusy(false);
    }
  };

  // ---------- Save & continue later (magic-link email) ----------
  const [saveLinkOpen, setSaveLinkOpen] = useState(false);
  const [saveLinkEmail, setSaveLinkEmail] = useState(user?.email || submitter.email || "");
  const [saveLinkBusy, setSaveLinkBusy] = useState(false);
  const requestSaveLink = async () => {
    if (!saveLinkEmail.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(saveLinkEmail.trim())) {
      toast.error("Enter a valid email address.");
      return;
    }
    setSaveLinkBusy(true);
    try {
      // Strip data URLs (portraits, cover) so we don't blow the payload up.
      const { portraits, cover_image, ...formLite } = form; // eslint-disable-line no-unused-vars
      const draftBundle = {
        form: formLite,
        spouses, relationships, timeline, charities,
        familyDetails, bioDetails, services, funeralProgram,
        audioTracks, legacy, places, genealogyLinks, privacyMap, step,
      };
      await http.post("/celebrate/save-draft", {
        email: saveLinkEmail.trim(),
        person_name: form.full_name || null,
        form: draftBundle,
      });
      toast.success(`We emailed a resume link to ${saveLinkEmail.trim()}. Check your inbox.`);
      setSaveLinkOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save the draft — please try again.");
    } finally {
      setSaveLinkBusy(false);
    }
  };

  // On mount, if the URL has ?draft=<token>, hydrate from server-side draft.
  useEffect(() => {
    const draftToken = sp.get("draft");
    if (!draftToken) return;
    (async () => {
      try {
        const { data } = await http.get(`/celebrate/draft/${encodeURIComponent(draftToken)}`);
        const b = data?.form || {};
        if (b.form) setForm((f) => ({ ...f, ...b.form }));
        if (b.spouses) setSpouses(b.spouses);
        if (b.relationships) setRelationships(b.relationships);
        if (b.timeline) setTimeline(b.timeline);
        if (b.charities) setCharities(b.charities);
        if (b.familyDetails) setFamilyDetails(b.familyDetails);
        if (b.bioDetails) setBioDetails((x) => ({ ...x, ...b.bioDetails }));
        if (b.services) setServices((x) => ({ ...x, ...b.services }));
        if (b.funeralProgram) setFuneralProgram((x) => ({ ...x, ...b.funeralProgram }));
        if (b.audioTracks) setAudioTracks(b.audioTracks);
        if (b.legacy) setLegacy((x) => ({ ...x, ...b.legacy }));
        if (b.places) setPlaces(b.places);
        if (b.genealogyLinks) setGenealogyLinks((x) => ({ ...x, ...b.genealogyLinks }));
        if (b.privacyMap) setPrivacyMap(b.privacyMap);
        if (b.step) setStep(Math.min(3, Math.max(1, Number(b.step) || 1)));
        toast.success("Draft restored from your email link — pick up where you left off.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load the saved draft.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    http.get("/memorials/config").then((r) => setCfg(r.data)).catch(() => {});
    loadRecaptcha().catch(() => {});
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPrivacy = (section, v) => setPrivacyMap((m) => ({ ...m, [section]: v }));

  const addPortrait = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (form.portraits.length + files.length > 5) {
      toast.error("Maximum 5 photos");
      return;
    }
    const readers = files.map((file) => {
      if (file.size > 3 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 3MB`);
        return null;
      }
      return new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.readAsDataURL(file);
      });
    }).filter(Boolean);
    Promise.all(readers).then((imgs) => {
      set("portraits", [...form.portraits, ...imgs]);
    });
    e.target.value = ""; // reset input
  };

  const removePortrait = (i) => {
    const next = form.portraits.filter((_, idx) => idx !== i);
    setForm((f) => ({
      ...f,
      portraits: next,
      primary_portrait_index: Math.max(0, Math.min(f.primary_portrait_index, next.length - 1)),
    }));
  };

  const onCover = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) return toast.error("Cover must be under 3MB");
    const r = new FileReader();
    r.onload = () => set("cover_image", r.result);
    r.readAsDataURL(file);
  };

  const runSuggest = async () => {
    if (!form.full_name.trim()) {
      toast.error("Enter the name first, then we'll look for connections.");
      return;
    }
    setSuggesting(true);
    try {
      const { data } = await http.get("/memorials/family-tree-suggestions", {
        params: {
          full_name: form.full_name.trim(),
          birth_date: form.birth_date || undefined,
          death_date: form.death_date || undefined,
          birth_place: form.birth_place || undefined,
        },
      });
      setSuggestions(data);
      const hits = (data.matched_persons?.length || 0) + (data.matched_trees?.length || 0) + (data.submitter_trees?.length || 0);
      if (!hits) toast.info("No family tree matches yet. You can create one after publishing.");
    } catch {
      toast.error("Suggestion lookup failed");
    } finally {
      setSuggesting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept the terms."); return; }
    if (!form.full_name.trim()) { toast.error("Full name is required."); return; }
    if (!form.location.country) { toast.error("Country is required."); return; }
    if (!form.birth_date && !form.age_at_death) {
      toast.error("Either date of birth OR age at death is required.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test((submitter.email || "").trim())) {
      toast.error("Please enter a valid email address in Your Details.");
      return;
    }

    const payload = {
      memorial_type: form.memorial_type,
      title: form.title || undefined,
      full_name: form.full_name.trim(),
      preferred_name: form.preferred_name || undefined,
      known_as: form.known_as || undefined,
      nicknames: form.nicknames ? form.nicknames.split(",").map((s) => s.trim()).filter(Boolean) : [],
      maiden_name: form.maiden_name || undefined,
      family_name: form.family_name || undefined,
      other_names: form.other_names ? form.other_names.split(",").map((s) => s.trim()).filter(Boolean) : [],
      portraits: form.portraits,
      portrait: form.portraits[form.primary_portrait_index] || form.portraits[0] || undefined,
      primary_portrait_index: form.primary_portrait_index,
      cover_image: form.cover_image || undefined,
      quote: form.quote || undefined,
      short_bio: form.short_bio || undefined,
      location: form.location,
      birth_date: form.birth_date || undefined,
      birth_place: form.birth_place || undefined,
      death_date: form.death_date || undefined,
      death_place: form.death_place || undefined,
      age_at_death: form.age_at_death ? Number(form.age_at_death) : undefined,
      cause_of_death: form.cause_of_death || undefined,
      gender: form.gender || undefined,
      nationality: form.nationality || undefined,
      citizenship: form.citizenship || undefined,
      occupation: form.occupation || undefined,
      profession: form.profession || undefined,
      education: form.education || undefined,
      military_service: form.military_service || undefined,
      religion: form.religion || undefined,
      languages: form.languages ? form.languages.split(",").map((s) => s.trim()).filter(Boolean) : [],
      is_married: form.is_married,
      obituary: form.obituary || undefined,
      biography: form.biography || undefined,
      timeline: timeline.filter((t) => t.title).map((t) => ({ ...t, year: t.year ? Number(t.year) : undefined })),
      relationships: [
        ...spouses.filter((s) => s.name).map((s) => ({ relation: s.is_former ? "former_spouse" : "spouse", name: s.name })),
        ...relationships.filter((r) => r.name),
      ],
      privacy: form.privacy,
      privacy_settings: Object.keys(privacyMap).length ? privacyMap : undefined,
      funeral: {
        home: form.funeral_home || undefined,
        date: form.funeral_date || undefined,
        time: form.funeral_time || undefined,
        location: form.funeral_location || undefined,
        livestream_url: form.funeral_livestream || undefined,
        dress_code: form.funeral_dress || undefined,
        flowers_accepted: form.flowers_accepted === "yes",
        donations_preferred: form.donations_preferred === "yes",
      },
      cemetery: {
        name: form.cemetery_name || undefined,
        section: form.cemetery_section || undefined,
        plot: form.cemetery_plot || undefined,
      },
      services: Object.keys(services).some((k) => services[k]) ? services : undefined,
      funeral_program: Object.keys(funeralProgram).some((k) => funeralProgram[k]) ? funeralProgram : undefined,
      family_details: Object.keys(familyDetails).length ? familyDetails : undefined,
      bio_details: Object.keys(bioDetails).some((k) => bioDetails[k]) ? bioDetails : undefined,
      audio: audioTracks.filter((a) => a.title || a.url),
      legacy_library: Object.keys(legacy).some((k) => legacy[k])
        ? Object.fromEntries(
            Object.entries(legacy)
              .filter(([, v]) => v)
              .map(([k, v]) => [k, v.split(",").map((s) => s.trim()).filter(Boolean)])
          )
        : undefined,
      places_of_significance: places.filter((p) => p.name),
      external_links: Object.entries(genealogyLinks)
        .filter(([, v]) => v && v.trim())
        .map(([kind, url]) => ({ kind, url: url.trim() })),
      charities: charities.filter((c) => c.name),
      family_tree_slug: form.family_tree_slug || undefined,
      linked_sp_id: form.linked_sp_id || undefined,
      submitter,
      consent: true,
    };
    setBusy(true);
    try {
      if (RECAPTCHA_SITE_KEY) {
        try {
          const gre = await loadRecaptcha();
          if (gre) payload.captcha_token = await gre.execute(RECAPTCHA_SITE_KEY, { action: "memorial_submit" });
        } catch { /* soft-fail */ }
      }
      const { data } = await http.post("/memorials", payload);
      toast.success("Thank you — the memorial has been submitted.");
      clearDraft();
      // Family Tree CTA: prefer deceased-linked tree > submitter tree > "create one"
      const cta = data.family_tree_cta || {};
      setCtaModal({
        submitter_tree: cta.submitter_tree,
        deceased_tree: cta.deceased_tree,
        slug: data.slug,
        status: data.status,
        person_name: form.full_name.trim(),
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed");
    } finally {
      setBusy(false);
    }
  };

  const currentStep = STEPS[step - 1];
  const stepIndexInVisible = stepsVisible.findIndex((s) => s.n === step);
  const progressPct = Math.round(((stepIndexInVisible + 1) / stepsVisible.length) * 100);

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-10" data-testid="celebrate-submit-page">
      {/* "Visit the Memorial Wall" nudge — encourage visitors to see the
          existing tributes before creating one so they feel the community
          before committing. */}
      <Link
        to="/celebrate/browse"
        data-testid="visit-memorial-wall-link"
        className="flex items-center justify-center gap-2 mb-6 rounded-2xl border px-4 py-3 text-sm md:text-base font-semibold transition hover:shadow-md"
        style={{
          borderColor: "var(--wofy-primary, #C2410C)",
          color:        "var(--wofy-primary, #C2410C)",
          background:  "#FFF7ED",
        }}
      >
        <Flower className="w-4 h-4" />
        <span>Visit the Memorial Wall</span>
        <ChevronRight className="w-4 h-4" />
      </Link>

      {/* Header */}
      <div className="text-center mb-6">
        <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>CELEBRATE A LIFE · ETERNAL MEMORIALS</div>
        <h1 className="font-display text-3xl md:text-5xl leading-tight">
          {step === 1
            ? (form.memorial_type === "death_announcement" ? "Announce a passing" : "Create an Eternal Memorial")
            : (form.full_name || "A tribute")}
        </h1>
        <div
          className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold"
          style={{ background: "#DCFCE7", color: "#166534" }}
          data-testid="free-badge"
        >
          <Check className="w-3 h-3" /> 100% Free — no credit card, ever
        </div>
        {step === 1 && (
          <p className="mt-3 text-sm max-w-xl mx-auto" style={{ color: "var(--wofy-text-soft)" }}>
            Three gentle steps: Remember · Their story · Publish. Save and return anytime.
          </p>
        )}
      </div>

      {/* Progress bar + stepper */}
      <div className="mb-6" data-testid="wizard-progress">
        <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider mb-2" style={{ color: "var(--wofy-text-soft)" }}>
          <span>Step {stepIndexInVisible + 1} of {stepsVisible.length} — {currentStep.label}</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => { setSaveLinkEmail(user?.email || submitter.email || ""); setSaveLinkOpen(true); }}
              className="underline normal-case hover:opacity-100 opacity-80"
              style={{ color: "var(--wofy-primary)" }}
              data-testid="wizard-save-later"
            >
              Save & continue later
            </button>
            <span>{progressPct}%</span>
          </div>
        </div>
        <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: "var(--wofy-border)" }}>
          <div
            className="h-full transition-all duration-500"
            style={{ width: `${progressPct}%`, background: "var(--wofy-primary)" }}
          />
        </div>
        {/* Compact step dots — clickable once passed */}
        <div className="mt-3 flex items-center justify-between md:justify-center md:gap-6 flex-wrap">
          {stepsVisible.map((s) => {
            const done = s.n < step;
            const active = s.n === step;
            return (
              <button
                type="button"
                key={s.n}
                onClick={() => (done || active) && goTo(s.n)}
                disabled={!done && !active}
                data-testid={`wizard-step-${s.key}`}
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider disabled:opacity-40"
                style={{ color: active ? "var(--wofy-primary)" : done ? "var(--wofy-text-soft)" : "var(--wofy-text-soft)" }}
              >
                <span
                  className="w-5 h-5 rounded-full inline-flex items-center justify-center border"
                  style={{
                    background: active ? "var(--wofy-primary)" : done ? "var(--wofy-primary)" : "transparent",
                    color: (active || done) ? "white" : "var(--wofy-text-soft)",
                    borderColor: active || done ? "var(--wofy-primary)" : "var(--wofy-border)",
                  }}
                >
                  {done ? <Check className="w-3 h-3" /> : s.n}
                </span>
                <span className="hidden md:inline">{s.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Card frame */}
      <div className="wofy-card">
        <div className="mb-4">
          <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>STEP {stepIndexInVisible + 1} OF {stepsVisible.length}</div>
          <h2 className="font-display text-2xl md:text-3xl leading-tight">{currentStep.title}</h2>
          <p className="mt-1 text-sm" style={{ color: "var(--wofy-text-soft)" }}>{currentStep.encouragement}</p>
        </div>

        <form onSubmit={submit} className="space-y-6" onKeyDown={(e) => {
          // Prevent accidental submit on Enter while inside intermediate steps.
          if (e.key === "Enter" && e.target.tagName !== "TEXTAREA" && step !== STEPS.length) {
            e.preventDefault();
          }
        }}>

        {/* ============ Stage panels (compose the extracted step components) ============ */}
        {step === 1 && (
          <div className="space-y-6" data-testid="stage-remember">
            <Step1Who form={form} set={set} />
            <div className="pt-5 border-t" style={border}>
              <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>A LITTLE ABOUT THEIR LIFE</div>
              <Step2Life form={form} set={set} />
            </div>
            {!isPhotoStepSkipped && (
              <div className="pt-5 border-t" style={border}>
                <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>SHARE A PHOTO (OPTIONAL)</div>
                <p className="text-[11px] mb-3" style={{ color: "var(--wofy-text-soft)" }}>
                  A photo helps family recognise them — you can also add it later.
                </p>
                <Step3Photo form={form} set={set}
                  addPortrait={addPortrait} removePortrait={removePortrait} onCover={onCover} />
              </div>
            )}
          </div>
        )}
        {step === 2 && (
          <div className="space-y-6" data-testid="stage-story">
            <Step4Story form={form} set={set}
              aiSuggestions={aiSuggestions} aiBusy={aiBusy} requestAiBio={requestAiBio} />
            <OptionalAccordion title="Family & genealogy — link a family tree (optional)" testid="acc-family">
              <Step5Family
                form={form} set={set}
                spouses={spouses} setSpouses={setSpouses}
                familyDetails={familyDetails} setFamilyDetails={setFamilyDetails}
                runSuggest={runSuggest} suggesting={suggesting} suggestions={suggestions}
                genealogyLinks={genealogyLinks} setGenealogyLinks={setGenealogyLinks}
                relationships={relationships} setRelationships={setRelationships}
                cfg={cfg}
              />
            </OptionalAccordion>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-6" data-testid="stage-publish">
            <MemorialLivePreview form={form} />
            <Step7Review
              form={form} set={set} goTo={goTo}
              submitter={submitter} setSubmitter={setSubmitter}
              genealogyLinks={genealogyLinks}
              spouses={spouses} relationships={relationships}
              timeline={timeline} places={places}
              audioTracks={audioTracks} charities={charities}
              RECAPTCHA_SITE_KEY={RECAPTCHA_SITE_KEY}
            />
            <OptionalAccordion title="More details — funeral, services, timeline, legacy (all optional)" testid="acc-more-details">
              <Step6Extras
                form={form} set={set} cfg={cfg}
                bioDetails={bioDetails} setBioDetails={setBioDetails}
                audioTracks={audioTracks} setAudioTracks={setAudioTracks}
                legacy={legacy} setLegacy={setLegacy}
                places={places} setPlaces={setPlaces}
                timeline={timeline} setTimeline={setTimeline}
                services={services} setServices={setServices}
                funeralProgram={funeralProgram} setFuneralProgram={setFuneralProgram}
                charities={charities} setCharities={setCharities}
              />
            </OptionalAccordion>
          </div>
        )}

        {/* ============ Wizard nav buttons ============ */}
        <div className="pt-2 border-t flex items-center justify-between gap-3" style={border}>
          <button
            type="button"
            onClick={back}
            disabled={step === 1}
            data-testid="wizard-back"
            className="wofy-btn-ghost disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          {step < STEPS.length ? (
            <button
              type="button"
              onClick={next}
              data-testid="wizard-next"
              className="wofy-btn-primary"
            >
              {reviewEditFor ? "Save & return to preview" : "Continue"}
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button disabled={busy} className="wofy-btn-primary" data-testid="mem-submit">
              {busy ? "Publishing…" : (<><Flower className="w-4 h-4" /> Publish memorial — Free</>)}
            </button>
          )}
        </div>

        </form>
      </div>

      <p className="text-[10px] font-mono text-center mt-4 opacity-60">
        Your progress is auto-saved locally — feel free to leave and come back.
      </p>

      {/* After-submit CTA modal */}
      {ctaModal && (
        <FamilyTreeCtaModal
          modal={ctaModal}
          onClose={() => {
            const slug = ctaModal.slug;
            setCtaModal(null);
            if (ctaModal.status === "approved") nav(`/celebrate/${slug}`);
            else nav("/celebrate");
          }}
        />
      )}

      {/* Save-and-continue-later magic-link modal */}
      {saveLinkOpen && (
        <div
          className="fixed inset-0 z-[70] bg-[#14213D]/50 backdrop-blur-sm flex items-start justify-center p-4 pt-28 overflow-y-auto"
          onClick={() => setSaveLinkOpen(false)}
          data-testid="save-later-backdrop"
        >
          <div
            className="wofy-card w-full max-w-md max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="save-later-modal"
          >
            <div className="overline mb-2">SAVE & CONTINUE LATER</div>
            <h3 className="font-display text-2xl mb-1">Send yourself a link</h3>
            <p className="text-sm mb-4" style={{ color: "var(--wofy-text-soft)" }}>
              We'll email you a private link that resumes this exact tribute — including everything you've typed so far. Link is valid for 30 days.
            </p>
            <label className="block mb-3">
              <div className="text-[11px] font-mono uppercase tracking-wider mb-1">Your email</div>
              <input
                type="email"
                value={saveLinkEmail}
                onChange={(e) => setSaveLinkEmail(e.target.value)}
                placeholder="you@example.com"
                className="wofy-input"
                autoFocus
                data-testid="save-later-email"
              />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setSaveLinkOpen(false)} className="wofy-btn-ghost" data-testid="save-later-cancel">
                Cancel
              </button>
              <button
                type="button"
                onClick={requestSaveLink}
                disabled={saveLinkBusy}
                className="wofy-btn-primary disabled:opacity-60"
                data-testid="save-later-submit"
              >
                {saveLinkBusy ? "Sending…" : "Email me the link"}
              </button>
            </div>
            <p className="text-[10px] mt-3 opacity-60">
              Photos aren't included in the emailed draft (they're safe locally in your browser). Once you resume the link, re-upload the primary photo if needed.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

// Instant preview of the memorial hero, updating live as the user types.
function MemorialLivePreview({ form }) {
  const photo = form.portraits?.[form.primary_portrait_index] || form.portraits?.[0];
  const dates = [(form.birth_date || "").slice(0, 4), (form.death_date || "").slice(0, 4)]
    .filter(Boolean).join(" – ");
  return (
    <div className="rounded-2xl border overflow-hidden" style={border} data-testid="memorial-live-preview">
      <div
        className="px-4 py-2 text-[10px] font-mono uppercase tracking-wider border-b flex items-center gap-2"
        style={{ borderColor: "var(--wofy-border)", background: "#FFF7ED", color: "var(--wofy-primary)" }}
      >
        <Flower className="w-3 h-3" /> Instant preview — how the memorial will appear
      </div>
      <div className="p-6 text-center" style={{ background: "#FFFDF9" }}>
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] mb-3" style={{ color: "var(--wofy-text-soft)" }}>
          In loving memory
        </div>
        <div className="flex justify-center mb-3">
          {photo ? (
            <img src={photo} alt="" className="w-24 h-24 rounded-full object-cover border-2" style={{ borderColor: "var(--wofy-primary)" }} />
          ) : (
            <InitialsAvatar name={form.full_name || "?"} gender={form.gender} size={96} />
          )}
        </div>
        <div className="font-display text-2xl" data-testid="live-preview-name">{form.full_name || "Their name"}</div>
        {dates && <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>{dates}</div>}
        {form.quote && <p className="italic text-sm mt-2">“{form.quote}”</p>}
        {form.short_bio && (
          <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: "var(--wofy-text-soft)" }}>{form.short_bio}</p>
        )}
      </div>
    </div>
  );
}
